import FilterComponent from '@/components/common/FilterComponent';
import MainHeading from '@/components/common/MainHeading';
import Layout from '@/components/Layout/Layout';
import useFilter from '@/hooks/useFilter';
import { IDeleteResponse, IPageInfo } from '@/interfaces/common.model';
import moment from 'moment';
import { useEffect, useState } from 'react';
import AttendanceTable from './AttendanceTable';
import { callApi, IApiErrorResponse } from '@/api/callApi';
import { getFilterUrl, showToastNotification } from '@/utility/helper';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useDispatch } from 'react-redux';
import {
  setDateFilter,
  setUpdateFilterStates,
} from '@/redux/features/UserSlice';
import FullPageLoader from '@/components/loader/FullPageLoader';
import { useNavigate } from 'react-router-dom';
import { IUserAttendance } from '@/interfaces/attendance.model';
import CardLayout from '@/components/Layout/CardLayout';
import ExportDataHandler from '@/utility/CommonFunctions';
interface IPagination extends IPageInfo {
  count: number;
  searchQuery: string;
}
interface IPageState {
  tableLoader: boolean;
}
interface IBulkAttState {
  attendanceList: IUserAttendance[];
}
interface IBulkAttResponse {
  status: boolean;
  data: { attendance: IUserAttendance[]; pageInfo: IPageInfo };
}
const ManageBulkAtt = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetails = useSelector((state: RootState) => state.user.user);
  const dateFilter = useSelector((state: RootState) => state?.user?.months);
  const filterDetails = useSelector(
    (state: RootState) => state.user?.filtersStates,
  );
  const tableView = useSelector((state: RootState) => state?.user?.tableView);
  const [initialLoader, setInitialLoader] = useState<boolean>(true);
  const [pagination, setPagination] = useState<IPagination>({
    currentPage: 1,
    count: 0,
    totalPages: 0,
    searchQuery: '',
  });
  const [state, setState] = useState<IBulkAttState>({
    attendanceList: [],
  });
  const [pageStates, setPageStates] = useState<IPageState>({
    tableLoader: false,
  });
  useEffect(() => {
    getAttendance(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    getAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateFilter,
    pagination.searchQuery,
    filterDetails?.currentPage,
    filterDetails?.pageSize,
  ]);

  const getAttendance = async (isInitial: boolean = false) => {
    try {
      if (isInitial) {
        setInitialLoader(true);
      } else {
        setPageStates((pre) => ({ ...pre, tableLoader: true }));
      }
      const filterData = {
        pageNumber: filterDetails.currentPage,
        pageSize: filterDetails?.pageSize,
        search: pagination?.searchQuery,
        startDate: dateFilter[0],
        endDate: dateFilter[1],
      };

      const API =
        API_URL +
        API_ENDPOINT.getAttendance +
        '?from=todayAtt&' +
        getFilterUrl(filterData);

      const response: IBulkAttResponse = await callApi(API, { method: 'GET' });
      if (response?.status) {
        setState((pre) => ({
          ...pre,
          attendanceList: response.data.attendance,
        }));
        setPagination((prev) => ({
          ...prev,
          count: response?.data?.pageInfo?.total || 0,
          totalPages: response?.data?.pageInfo?.totalPages || 0,
        }));
        dispatch(
          setUpdateFilterStates({
            count: response?.data?.pageInfo?.total,
            totalPages: response?.data?.pageInfo?.totalPages,
          }),
        );
      }
    } catch (error) {
      showToastNotification(
        'danger',
        (error as IApiErrorResponse)?.message || 'Something went wrong',
      );
    } finally {
      if (isInitial) {
        setInitialLoader(false);
      } else {
        setPageStates((pre) => ({ ...pre, tableLoader: false }));
      }
    }
  };

  const singleDateChangeHandler = (date: Date | null) => {
    if (!date || !moment(date).isValid()) return;

    const selectedStartDate = moment(date)
      .startOf('month')
      .format('YYYY-MM-DD');
    const selectedEndDate = moment(date).endOf('month').format('YYYY-MM-DD');
    dispatch(setDateFilter([selectedStartDate, selectedEndDate]));
  };

  const handleChangeAttendance = async (
    newStatus: number,
    userId: string,
    userName: string,
    date: string,
  ) => {
    try {
      if (newStatus !== 3) {
        const submitData = {
          userId,
          markingAttendance: true,
          status: newStatus,
          date,
        };
        const updateList = state.attendanceList.map((att) => {
          if (att.id === att?.id) {
            return {
              ...att,
              status: newStatus,
            };
          }
          return att;
        });
        // const updateList = state.attendanceList.map((att) => {
        //   if (att.id === userId) {
        //     return {
        //       ...att,
        //       status: newStatus,
        //     };
        //   }
        //   return att;
        // });
        const response: IDeleteResponse = await callApi(
          API_URL + API_ENDPOINT?.markAttendance,
          {
            method: 'POST',
            data: submitData,
          },
        );

        if (response?.status === true) {
          showToastNotification('success', 'Attendance Updated');
          setState((prev) => ({ ...prev, attendanceList: updateList }));
          getAttendance();
        } else {
          showToastNotification('danger', response?.message);
        }
      } else {
        navigate('/add-leave', {
          state: {
            userId,
            userName,
            type: 'remaining',
            sortBySingleDate: [date, date],
            markAttendance: true,
            source: 'manageBulk',
          },
        });
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    }
  };
  const { handleSearchChange, pageChangeHandler, handleChangePageSize } =
    useFilter(pagination, setPagination);
  const handleExport = () => {
    const data = {
      userType: 0,
      exportDataType: 'bulkAttendance',
      startDate: dateFilter?.[0],
      endDate: dateFilter?.[1],
      department: filterDetails?.department,
      designation: filterDetails?.designation,
    };
    ExportDataHandler(data);
  };
  return (
    <Layout>
      {initialLoader ? (
        <FullPageLoader />
      ) : (
        <div className="content">
          <MainHeading
            title={'Manage Bulk Attendance'}
            showDefaultDate={true}
            singleDateChangeHandler={singleDateChangeHandler}
            pickerType="month"
            pageState={{
              sortBySingleDate: dateFilter,
            }}
          />
          <CardLayout isGrid={tableView === 'grid'}>
            <FilterComponent
              handleExport={
                state?.attendanceList.length > 0 ? handleExport : null
              }
              handleSearch={handleSearchChange}
              searchPlaceHolder="Search"
              isGrid={false}
            />
            {pageStates.tableLoader ? (
              <div className="d-flex justify-content-center p-5">
                <FullPageLoader />
              </div>
            ) : (
              <AttendanceTable
                currentPage={filterDetails?.currentPage}
                isLoading={initialLoader}
                count={filterDetails?.count || 0}
                pageSize={filterDetails?.pageSize || 10}
                totalPages={filterDetails?.totalPages || 0}
                handleChangePageSize={handleChangePageSize}
                pageChangeHandler={pageChangeHandler}
                attendanceList={state.attendanceList}
                dateFilter={dateFilter}
                userDetails={userDetails}
                handleChangeAttendance={handleChangeAttendance}
              />
            )}
          </CardLayout>
        </div>
      )}
    </Layout>
  );
};

export default ManageBulkAtt;
